import { useMemo } from "react";
import { formatCountdown } from "@cede/utils";
import type { EpochSelectorItem } from "@/components/shared/EpochSelector/types";
import { EpochStatus } from "@/constants";
import { useCountdown } from "@/hooks/core/useCountdown";
import { EPOCH_STATUS_META, getCustomEpochLabel, getEpochDisplayNumber } from "@/utils/campaign/epochSelector";

/**
 * Computes display metadata for an epoch including countdown timers and status text.
 *
 * This hook:
 * - Calculates live countdown to epoch start (for pending) or end (for active epochs)
 * - Formats countdown as compact human-readable text (e.g., "2d 3h", "45m 12s")
 * - Generates appropriate status text ("Starts in...", "Ends in...", "Ended")
 * - Provides status metadata (label, color) for UI display
 * - Handles custom epoch labels and display numbers
 *
 * @param epoch - The epoch selector item to compute metadata for
 *
 * @returns Object containing:
 * - `countdown` - Live countdown object with days, hours, minutes, seconds
 * - `countdownTarget` - The target Date for the countdown (epoch start or end)
 * - `statusText` - Formatted status text for display (e.g., "Ends in 2d 3h")
 * - `statusMeta` - Status metadata object with label and color
 * - `displayNumber` - The epoch's display number (1-indexed)
 * - `customLabel` - Custom epoch label if different from default
 *
 * @example
 * ```tsx
 * const { statusText, countdown, statusMeta } = useEpochDisplayMetadata(selectedEpoch);
 *
 * return (
 *   <div className={statusMeta?.color}>
 *     <span>{statusText}</span>
 *     {countdown && <Timer countdown={countdown} />}
 *   </div>
 * );
 * ```
 */
export const useEpochDisplayMetadata = (epoch?: EpochSelectorItem | null) => {
  const countdownTarget = useMemo(() => {
    if (!epoch) return undefined;
    if (epoch.status === EpochStatus.ACTIVE) {
      return epoch.end;
    }
    if (epoch.status === EpochStatus.PENDING) {
      return epoch.start;
    }
    return undefined;
  }, [epoch]);

  const countdown = useCountdown(countdownTarget);
  const statusMeta = epoch?.status ? EPOCH_STATUS_META[epoch.status] : undefined;

  const statusText = useMemo(() => {
    if (!epoch) return "";

    if (epoch.status === EpochStatus.COMPLETED) {
      return "Ended";
    }

    if ((epoch.status === EpochStatus.ACTIVE || epoch.status === EpochStatus.PENDING) && countdown) {
      const countdownStr = formatCountdown(countdown, "compact-smart");
      if (countdownStr) {
        return `${epoch.status === EpochStatus.ACTIVE ? "Ends" : "Starts"} in ${countdownStr}`;
      }
    }

    return statusMeta?.label ?? "";
  }, [epoch, countdown, statusMeta?.label]);

  // Formatted countdown string without prefix (for card display)
  const countdownFormatted = useMemo(() => {
    if (!epoch || !countdown) return undefined;
    const countdownStr = formatCountdown(countdown, "compact-smart");
    return countdownStr ? `in ${countdownStr}` : undefined;
  }, [epoch, countdown]);

  // Card label based on epoch status
  const cardLabel = useMemo(() => {
    if (!epoch) return "Epoch Reset";
    if (epoch.status === EpochStatus.PENDING) return "Epoch Starts";
    if (epoch.status === EpochStatus.PAUSED) return "Epoch Paused";
    if (epoch.status === EpochStatus.ACTIVE) return "Epoch Reset";
    return "Epoch Reset";
  }, [epoch]);

  return {
    countdown,
    countdownTarget,
    statusText,
    countdownFormatted,
    cardLabel,
    statusMeta,
    displayNumber: epoch ? getEpochDisplayNumber(epoch) : undefined,
    customLabel: epoch ? getCustomEpochLabel(epoch) : undefined,
  };
};
